// Chart-specific field-well labels + field-type helpers for the data pane / inspector.
import { Type, Hash, Calendar, ToggleLeft, DollarSign, Percent, Sigma, FunctionSquare, ListTree } from "lucide-react";

// Per widget type: how the generic dimensions/measures arrays should be presented to authors.
const WELLS = {
  bar: { dimsTitle: "X-Axis (Category)", dimLabels: ["X-Axis (Column)", "Row (group / series)"], measTitle: "Y-Axis (Values)" },
  bar_stacked: { dimsTitle: "X-Axis (Category)", dimLabels: ["X-Axis (Column)", "Row (group / series)"], measTitle: "Y-Axis (Values)" },
  line: { dimsTitle: "X-Axis", dimLabels: ["X-Axis", "Row (group / series)"], measTitle: "Y-Axis (Values)" },
  area: { dimsTitle: "X-Axis", dimLabels: ["X-Axis", "Row (group / series)"], measTitle: "Y-Axis (Values)" },
  combo: { dimsTitle: "X-Axis", dimLabels: ["X-Axis", "Row (group / series)"], measTitle: "Values (bar + line)" },
  pie: { dimsTitle: "Legend (Category)", dimLabels: ["Legend"], measTitle: "Values" },
  donut: { dimsTitle: "Legend (Category)", dimLabels: ["Legend"], measTitle: "Values" },
  funnel: { dimsTitle: "Stages (Category)", dimLabels: ["Stage"], measTitle: "Values" },
  radar: { dimsTitle: "Axes (Category)", dimLabels: ["Axis", "Legend / Series"], measTitle: "Values" },
  scatter: { dimsTitle: "Detail (Category)", dimLabels: ["Detail"], measTitle: "X / Y Values" },
  heatmap: { dimsTitle: "Axes", dimLabels: ["X-Axis", "Y-Axis"], measTitle: "Intensity (Values)" },
  treemap: { dimsTitle: "Hierarchy levels", dimLabels: ["Level 1", "Level 2", "Level 3"], measTitle: "Size (Values)" },
  sunburst: { dimsTitle: "Hierarchy levels", dimLabels: ["Ring 1", "Ring 2", "Ring 3"], measTitle: "Size (Values)" },
  table: { dimsTitle: "Columns (group by)", dimLabels: [], measTitle: "Values" },
  pivot: { dimsTitle: "Rows & Columns (use the Row/Col toggle)", dimLabels: [], measTitle: "Values" },
  kpi: { dimsTitle: "Compare by", dimLabels: [], measTitle: "Value" },
  gauge: { dimsTitle: "Category", dimLabels: [], measTitle: "Value" },
  liquidfill: { dimsTitle: "Category", dimLabels: [], measTitle: "Value" },
  geo_map: { dimsTitle: "Location (country)", dimLabels: ["Location"], measTitle: "Values" },
  bubble_map: { dimsTitle: "Location (country)", dimLabels: ["Location"], measTitle: "Bubble size (Values)" },
  point_map: { dimsTitle: "Geo fields", dimLabels: ["Label", "Latitude", "Longitude"], measTitle: "Bubble size / metric" },
  google_map: { dimsTitle: "Geo fields", dimLabels: ["Label", "Latitude", "Longitude"], measTitle: "Bubble size / metric" },
  sankey: { dimsTitle: "Flow (From → To)", dimLabels: ["From", "To"], measTitle: "Weight (Values)" },
  graph: { dimsTitle: "Nodes (From → To)", dimLabels: ["From", "To"], measTitle: "Link weight" },
  candlestick: { dimsTitle: "Date (X-Axis)", dimLabels: ["Date"], measTitle: "OHLC Values" },
  boxplot: { dimsTitle: "Category", dimLabels: ["Category"], measTitle: "Distribution" },
  calendar_heatmap: { dimsTitle: "Date", dimLabels: ["Date"], measTitle: "Values" },
  wordcloud: { dimsTitle: "Words", dimLabels: ["Word"], measTitle: "Weight" },
  pictorial_bar: { dimsTitle: "Category", dimLabels: ["Category"], measTitle: "Values" },
  image_scatter: { dimsTitle: "Detail", dimLabels: ["Detail"], measTitle: "X / Y Values" },
  waterfall: { dimsTitle: "Steps (Category)", dimLabels: ["Step"], measTitle: "Change (Values)" },
  tornado: { dimsTitle: "Category (Y-Axis)", dimLabels: ["Category"], measTitle: "Left / Right values (2 measures)" },
  data_grid: { dimsTitle: "Columns", dimLabels: [], measTitle: "—" },
  bar_3d: { dimsTitle: "Axes", dimLabels: ["X-Axis", "Y-Axis"], measTitle: "Height (Values)" },
  scatter_3d: { dimsTitle: "Detail", dimLabels: ["Detail"], measTitle: "X / Y / Z Values" },
};

export function getWells(type) {
  return WELLS[type] || { dimsTitle: "Dimensions (group by)", dimLabels: [], measTitle: "Measures (values)" };
}
export const dimLabelAt = (wells, i) => wells.dimLabels[i] || (wells.dimLabels.length ? `Level ${i + 1}` : null);

// ---- Field typing (respecting BI-layer overrides) ----
export const TYPE_OPTIONS = ["text", "number", "currency", "percent", "date", "datetime", "boolean"];
export const AGG_CHOICES = ["sum", "avg", "min", "max", "count", "count_distinct", "counta", "median", "product", "stddev", "stddevp", "var", "varp"];

export function effectiveType(fieldName, schema, fieldMeta) {
  const ov = fieldMeta?.[fieldName]?.type_override;
  if (ov) return ov;
  return (schema || []).find((c) => c.name === fieldName)?.type || "text";
}
export const isNumericType = (t) => ["number", "currency", "percent"].includes(t);
export const isDateType = (t) => ["date", "datetime"].includes(t);

export function typeIcon(t) {
  switch (t) {
    case "number": return Hash;
    case "currency": return DollarSign;
    case "percent": return Percent;
    case "date": case "datetime": return Calendar;
    case "boolean": return ToggleLeft;
    case "measure": return Sigma;
    case "calculated": return FunctionSquare;
    case "hierarchy": return ListTree;
    default: return Type;
  }
}

export const displayName = (fieldName, fieldMeta) => fieldMeta?.[fieldName]?.display_name || fieldName;
export const isHiddenField = (fieldName, fieldMeta) => !!fieldMeta?.[fieldName]?.hidden;
export const defaultAggFor = (fieldName, schema, fieldMeta) => {
  const explicit = fieldMeta?.[fieldName]?.default_agg;
  if (explicit) return explicit;
  return isNumericType(effectiveType(fieldName, schema, fieldMeta)) ? "sum" : "count";
};

// Drag-drop payload helpers (data pane -> canvas / field wells)
export const FIELD_MIME = "application/x-eip-field";
export function setFieldDrag(e, payload) {
  e.dataTransfer.setData(FIELD_MIME, JSON.stringify(payload));
  e.dataTransfer.effectAllowed = "copy";
}
export function readFieldDrag(e) {
  try {
    const raw = e.dataTransfer.getData(FIELD_MIME);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
